import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Eye,
  EyeOff,
  Loader2,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Upload,
  ImageIcon,
  ShieldCheck,
  User,
  IdCard,
  Lock,
  Gift,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch } from "@/lib/api";

type RegisterResponse = {
  ok?: boolean;
  message?: string;
};

type Errors = Partial<Record<
  | "referral_code"
  | "fname"
  | "mname"
  | "sname"
  | "phone_number"
  | "username"
  | "email"
  | "photo"
  | "id_side_1"
  | "id_side_2"
  | "password"
  | "password_confirmation",
  string
>>;

type StepKey = "basic" | "account" | "documents" | "security";

const steps: { key: StepKey; title: string; icon: React.ElementType }[] = [
  { key: "basic", title: "Basic Info", icon: User },
  { key: "account", title: "Account", icon: Gift },
  { key: "documents", title: "Documents", icon: IdCard },
  { key: "security", title: "Security", icon: Lock },
];

export default function RegisterPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);

  const [referralCode, setReferralCode] = useState("");
  const [fname, setFname] = useState("");
  const [mname, setMname] = useState("");
  const [sname, setSname] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");

  const [photo, setPhoto] = useState<File | null>(null);
  const [idSide1, setIdSide1] = useState<File | null>(null);
  const [idSide2, setIdSide2] = useState<File | null>(null);

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});

  const photoPreview = useMemo(
    () => (photo ? URL.createObjectURL(photo) : null),
    [photo]
  );
  const id1Preview = useMemo(
    () => (idSide1 ? URL.createObjectURL(idSide1) : null),
    [idSide1]
  );
  const id2Preview = useMemo(
    () => (idSide2 ? URL.createObjectURL(idSide2) : null),
    [idSide2]
  );

  const validateStep = (currentStep: number) => {
    const nextErrors: Errors = {};

    if (currentStep === 0) {
      if (!fname.trim()) nextErrors.fname = "First name is required";
      if (!sname.trim()) nextErrors.sname = "Surname is required";
      if (!phoneNumber.trim()) nextErrors.phone_number = "Phone number is required";
    }

    if (currentStep === 1) {
      if (!username.trim()) nextErrors.username = "Username is required";
      if (!email.trim()) nextErrors.email = "Email is required";
      else if (!/^\S+@\S+\.\S+$/.test(email)) nextErrors.email = "Enter a valid email";
    }

    if (currentStep === 2) {
      if (!photo) nextErrors.photo = "Profile photo is required";
      if (!idSide1) nextErrors.id_side_1 = "ID front side is required";
      if (!idSide2) nextErrors.id_side_2 = "ID back side is required";
    }

    if (currentStep === 3) {
      if (!password) nextErrors.password = "Password is required";
      else if (password.length < 6) nextErrors.password = "Password must be at least 6 characters";

      if (!passwordConfirmation) {
        nextErrors.password_confirmation = "Confirm your password";
      } else if (password !== passwordConfirmation) {
        nextErrors.password_confirmation = "Passwords do not match";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const nextStep = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const prevStep = () => {
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (file: File | null) => void
  ) => {
    const file = e.target.files?.[0] || null;
    setter(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateStep(3)) {
      setStep(3);
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("referral_code", referralCode);
      formData.append("fname", fname);
      formData.append("mname", mname);
      formData.append("sname", sname);
      formData.append("phone_number", phoneNumber);
      formData.append("username", username);
      formData.append("email", email);
      formData.append("password", password);
      formData.append("password_confirmation", passwordConfirmation);

      if (photo) formData.append("photo", photo);
      if (idSide1) formData.append("id_side_1", idSide1);
      if (idSide2) formData.append("id_side_2", idSide2);

      const data = await apiFetch<RegisterResponse>("/auth/register", {
        method: "POST",
        body: formData,
      });

      setSuccess(data?.message || "Registration successful. You can now log in.");

      setTimeout(() => {
        navigate("/login");
      }, 1200);
    } catch (e: any) {
      setError(e?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    if (step === 0) {
      return (
        <div className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="referral_code">Referral Code</Label>
              <Input
                id="referral_code"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                placeholder="Enter referral code (optional)"
                disabled={loading}
              />
              {errors.referral_code && (
                <p className="mt-1 text-xs text-destructive">{errors.referral_code}</p>
              )}
            </div>

            <div>
              <Label htmlFor="fname">First Name</Label>
              <Input
                id="fname"
                value={fname}
                onChange={(e) => setFname(e.target.value)}
                placeholder="Enter first name"
                disabled={loading}
              />
              {errors.fname && (
                <p className="mt-1 text-xs text-destructive">{errors.fname}</p>
              )}
            </div>

            <div>
              <Label htmlFor="mname">Middle Name</Label>
              <Input
                id="mname"
                value={mname}
                onChange={(e) => setMname(e.target.value)}
                placeholder="Enter middle name"
                disabled={loading}
              />
              {errors.mname && (
                <p className="mt-1 text-xs text-destructive">{errors.mname}</p>
              )}
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="sname">Surname</Label>
              <Input
                id="sname"
                value={sname}
                onChange={(e) => setSname(e.target.value)}
                placeholder="Enter surname"
                disabled={loading}
              />
              {errors.sname && (
                <p className="mt-1 text-xs text-destructive">{errors.sname}</p>
              )}
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="e.g. 07XXXXXXXX or 2547XXXXXXXX"
                disabled={loading}
              />
              {errors.phone_number && (
                <p className="mt-1 text-xs text-destructive">{errors.phone_number}</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="space-y-5">
          <div className="grid gap-5">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                disabled={loading}
              />
              {errors.username && (
                <p className="mt-1 text-xs text-destructive">{errors.username}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={loading}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-destructive">{errors.email}</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="space-y-5">
          <UploadField
            id="photo"
            label="Profile Photo"
            file={photo}
            preview={photoPreview}
            error={errors.photo}
            onChange={(e) => handleFileChange(e, setPhoto)}
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <UploadField
              id="id_side_1"
              label="ID Side 1"
              file={idSide1}
              preview={id1Preview}
              error={errors.id_side_1}
              onChange={(e) => handleFileChange(e, setIdSide1)}
            />

            <UploadField
              id="id_side_2"
              label="ID Side 2"
              file={idSide2}
              preview={id2Preview}
              error={errors.id_side_2}
              onChange={(e) => handleFileChange(e, setIdSide2)}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div>
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create password"
              disabled={loading}
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs text-destructive">{errors.password}</p>
          )}
        </div>

        <div>
          <Label htmlFor="password_confirmation">Confirm Password</Label>
          <div className="relative">
            <Input
              id="password_confirmation"
              type={showPassword2 ? "text" : "password"}
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              placeholder="Confirm password"
              disabled={loading}
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword2((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {showPassword2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password_confirmation && (
            <p className="mt-1 text-xs text-destructive">
              {errors.password_confirmation}
            </p>
          )}
        </div>

        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 text-primary" />
            <p>
              Your details and uploads will be used for account verification and
              secure onboarding.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-background relative overflow-hidden px-4 py-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.08),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.08),transparent_25%)]" />

      <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-6xl items-center">
        <div className="grid w-full gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          {/* Left side */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45 }}
            className="hidden lg:flex flex-col justify-center rounded-3xl border border-border bg-card/50 p-8 backdrop-blur"
          >
            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl gradient-primary">
              <Building2 className="h-8 w-8 text-primary-foreground" />
            </div>

            <h1 className="text-3xl font-bold leading-tight">
              Join GoldStep Capital and get started in minutes
            </h1>

            <p className="mt-4 max-w-md text-muted-foreground">
              A clean, step-by-step registration flow that makes onboarding feel simple,
              safe, and customer-friendly.
            </p>

            <div className="mt-8 space-y-4">
              {[
                "Simple multi-step registration",
                "Secure document upload",
                "Fast account onboarding",
                "Referral code support included",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right side */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="w-full"
          >
            <div className="rounded-3xl border border-border bg-card p-5 shadow-card sm:p-8">
              <div className="mb-6 flex flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl gradient-primary">
                  <UserPlus className="h-8 w-8 text-primary-foreground" />
                </div>
                <h2 className="text-2xl font-bold">Create Your Account</h2>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  Complete the quick steps below to register without overwhelming the customer.
                </p>
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    Step {step + 1} of {steps.length}
                  </span>
                  <span>{Math.round(progress)}%</span>
                </div>

                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div className="mt-4 grid grid-cols-4 gap-2">
                  {steps.map((item, index) => {
                    const Icon = item.icon;
                    const active = index === step;
                    const done = index < step;

                    return (
                      <div
                        key={item.key}
                        className={`rounded-xl border p-2 text-center transition-all ${
                          active
                            ? "border-primary bg-primary/5"
                            : done
                            ? "border-green-500/30 bg-green-500/5"
                            : "border-border bg-background"
                        }`}
                      >
                        <div
                          className={`mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded-full ${
                            active
                              ? "bg-primary text-primary-foreground"
                              : done
                              ? "bg-green-500 text-white"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {done ? (
                            <CheckCircle2 className="h-4 w-4" />
                          ) : (
                            <Icon className="h-4 w-4" />
                          )}
                        </div>
                        <p className="text-[11px] font-medium leading-tight">
                          {item.title}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {error && (
                <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 rounded-lg bg-green-500/10 p-3 text-sm text-green-600">
                  {success}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {renderStepContent()}

                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex gap-2">
                    {step > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={prevStep}
                        disabled={loading}
                        className="gap-2"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Back
                      </Button>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {step < steps.length - 1 ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        disabled={loading}
                        className="gap-2"
                      >
                        Continue
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button type="submit" disabled={loading} className="min-w-[160px] gap-2">
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Creating Account...
                          </>
                        ) : (
                          <>
                            Create Account
                            <CheckCircle2 className="h-4 w-4" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </form>

              <div className="mt-6 text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Login
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

type UploadFieldProps = {
  id: string;
  label: string;
  file: File | null;
  preview: string | null;
  error?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

function UploadField({
  id,
  label,
  file,
  preview,
  error,
  onChange,
}: UploadFieldProps) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>

      <label
        htmlFor={id}
        className="mt-2 flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/30 p-5 text-center transition hover:border-primary/40 hover:bg-primary/5"
      >
        {preview ? (
          <img
            src={preview}
            alt={label}
            className="mb-3 h-32 w-full max-w-[220px] rounded-xl object-cover"
          />
        ) : (
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <ImageIcon className="h-6 w-6 text-primary" />
          </div>
        )}

        <div className="flex items-center gap-2 text-sm font-medium">
          <Upload className="h-4 w-4" />
          <span>{file ? "Change file" : "Upload file"}</span>
        </div>

        <p className="mt-1 text-xs text-muted-foreground">
          {file ? file.name : "PNG, JPG or JPEG"}
        </p>
      </label>

      <Input
        id={id}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onChange}
      />

      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}